import type { Date as PDate } from "../../generated/core/v1/date";
import {
  type Event,
  Event_Type,
  IncomeDetails_Source,
  type RetirementDetails,
} from "../../generated/core/v1/events";
import { EntityKind, type State } from "../../generated/core/v1/financial_plan";
import type { Family } from "../../generated/core/v1/family";
import { compareDates, yearFraction } from "../lib/dates";
import { flatActions } from "./actions";
import { type Plan, entityKinds, eventsOf, INVESTMENT_KINDS, stateAt, yearlyStates } from "./core";

/**
 * Retirement from RETIREMENT events (the engine computes targets, levels and nest eggs) and the
 * plan's states, split at the retirement date.
 */
export type RetirementInfo = {
  event: Event;
  date: PDate;
  memberUuid: string;
  details: RetirementDetails;
};

export function retirementEvents(plan: Plan): RetirementInfo[] {
  return eventsOf(plan, Event_Type.RETIREMENT)
    .filter((e) => e.details.oneofKind === "retirement" && !!e.date)
    .map((e) => ({
      event: e,
      date: e.date!,
      memberUuid: e.memberUuid,
      details: (e.details as { oneofKind: "retirement"; retirement: RetirementDetails }).retirement,
    }));
}

/** The household's retirement: the first member's (the person who set up the plan), else the first. */
export function householdRetirement(
  plan: Plan,
  family: Family | undefined | null,
): RetirementInfo | undefined {
  const all = retirementEvents(plan);
  const self = family?.persons[0]?.uuid;
  return all.find((r) => r.memberUuid === self) ?? all[0];
}

export const RETIREMENT_ACCOUNT_KINDS = [
  EntityKind.NON_REGISTERED,
  EntityKind.RRSP,
  EntityKind.RRIF,
  EntityKind.DC_PENSION,
  EntityKind.TFSA,
];

export type KindRow = {
  year: number;
  values: Partial<Record<EntityKind, number>>;
  total: number;
  /** The part of the RRSP value held in the given group RRSPs (0 when none are given). */
  groupRrsp: number;
};

function kindRow(s: State, kinds: EntityKind[], groups: Set<string>): KindRow {
  const values: Partial<Record<EntityKind, number>> = {};
  let total = 0;
  kinds.forEach((k) => {
    const v = s.accounts.filter((a) => a.kind === k).reduce((x, a) => x + a.balance, 0);
    values[k] = v;
    total += v;
  });
  const groupRrsp = s.accounts
    .filter((a) => a.kind === EntityKind.RRSP && groups.has(a.entityUuid))
    .reduce((x, a) => x + a.balance, 0);
  if (values[EntityKind.RRSP] !== undefined) values[EntityKind.RRSP]! -= groupRrsp;
  return { year: s.date!.year, values, total, groupRrsp };
}

/**
 * Balances by account kind each year up to (accumulation) or from (decumulation) `split`. Group RRSPs
 * in `groups` come out of the RRSP value into `groupRrsp`.
 */
export function balancesByKind(
  plan: Plan,
  split: PDate | undefined,
  side: "before" | "after",
  kinds: EntityKind[] = RETIREMENT_ACCOUNT_KINDS,
  groups: Set<string> = new Set(),
): KindRow[] {
  return yearlyStates(plan)
    .filter((s) => {
      if (!split) return side === "before";
      const c = compareDates(s.date, split);
      return side === "before" ? c <= 0 : c >= 0;
    })
    .map((s) => kindRow(s, kinds, groups));
}

/** Where each retirement year's income comes from. */
export type IncomeSourceKey =
  | "cpp"
  | "oas"
  | "dbPension"
  | "dcPension"
  | "rrif"
  | "tfsa"
  | "nonreg"
  | "job"
  | "other";

export type IncomeRow = {
  year: number;
  values: Partial<Record<IncomeSourceKey, number>>;
  total: number;
};

const SOURCE_KEY: Partial<Record<IncomeDetails_Source, IncomeSourceKey>> = {
  [IncomeDetails_Source.CPP]: "cpp",
  [IncomeDetails_Source.OAS]: "oas",
  [IncomeDetails_Source.DB_PENSION]: "dbPension",
  [IncomeDetails_Source.DC_PENSION]: "dcPension",
  [IncomeDetails_Source.RRIF]: "rrif",
  [IncomeDetails_Source.JOB]: "job",
  [IncomeDetails_Source.OTHER_INCOME]: "other",
};

/** Retirement income by year from `from`: income events (gross) plus withdrawals from TFSA and non-registered accounts. */
export function retirementIncome(plan: Plan, from: PDate | undefined): IncomeRow[] {
  if (!plan || !from) return [];
  const rows = new Map<number, IncomeRow>();
  const row = (y: number) => {
    let r = rows.get(y);
    if (!r) {
      r = { year: y, values: {}, total: 0 };
      rows.set(y, r);
    }
    return r;
  };
  const add = (y: number, k: IncomeSourceKey, v: number) => {
    const r = row(y);
    r.values[k] = (r.values[k] ?? 0) + v;
    r.total += v;
  };
  eventsOf(plan, Event_Type.INCOME)
    .filter((e) => compareDates(e.date, from) >= 0 && e.details.oneofKind === "income")
    .forEach((e) => {
      if (e.details.oneofKind !== "income") return;
      const k = SOURCE_KEY[e.details.income.source];
      if (k) add(e.date!.year, k, e.details.income.gross);
    });
  const kinds = entityKinds(plan);
  flatActions(plan)
    .filter((f) => f.direction === "in" && compareDates(f.date, from) >= 0)
    .forEach((f) => {
      const k = kinds.get(f.targetUuid);
      if (k === EntityKind.TFSA) add(f.date.year, "tfsa", f.amount);
      else if (k === EntityKind.NON_REGISTERED) add(f.date.year, "nonreg", f.amount);
    });
  return [...rows.values()].sort((a, b) => a.year - b.year);
}

/** Net worth at the retirement date in a plan. */
export function balanceAt(plan: Plan, date: PDate | undefined): State | undefined {
  return stateAt(plan, date);
}

/**
 * Where the charts' retirement marker goes: the household's retirement date (the engine's
 * RETIREMENT event, from the retirement ages the household gave) as a fractional year, and its
 * year for the label ("Retirement, 2053"). Undefined when the plan has no retirement.
 */
export function retirementMarker(
  plan: Plan,
  family: Family | undefined | null,
): { x: number; year: number; date: PDate } | undefined {
  const r = householdRetirement(plan, family);
  return r ? { x: yearFraction(r.date), year: r.date.year, date: r.date } : undefined;
}

/** What's invested (registered and non-registered accounts) in a state: no cash, no property. */
export function investedTotal(s: State | undefined): number {
  return (s?.accounts ?? [])
    .filter((a) => INVESTMENT_KINDS.has(a.kind))
    .reduce((t, a) => t + a.balance, 0);
}

/**
 * Each invested account's balance each year, from the plan's first state up to `until` (Assets'
 * "What you have invested, year by year", stacked per account). Accounts the plan opens later get
 * a zero before they exist. Accounts are ordered by their balance at the end, largest first.
 */
export function investedByAccount(
  plan: Plan,
  until?: PDate,
): {
  uuids: string[];
  kinds: Map<string, EntityKind>;
  rows: { year: number; values: Record<string, number> }[];
} {
  const states = yearlyStates(plan).filter((s) => !until || compareDates(s.date, until) <= 0);
  const kinds = new Map<string, EntityKind>();
  states.forEach((s) =>
    s.accounts.forEach((a) => INVESTMENT_KINDS.has(a.kind) && kinds.set(a.entityUuid, a.kind)),
  );
  const rows = states.map((s) => {
    const values: Record<string, number> = {};
    kinds.forEach((_, u) => (values[u] = s.accounts.find((a) => a.entityUuid === u)?.balance ?? 0));
    return { year: s.date!.year, values };
  });
  const last = rows.at(-1)?.values ?? {};
  const uuids = [...kinds.keys()]
    .filter((u) => rows.some((r) => r.values[u] > 0.5))
    .sort((a, b) => (last[b] ?? 0) - (last[a] ?? 0));
  return { uuids, kinds, rows };
}
